import Administrator from "./Administrator";
import DBObject from "../utils/DBObject";
import CloneContext from "../utils/CloneContext";

export default class InvoiceConfiguration extends DBObject {
  private static readonly _ADMINISTRATOR: number = 0;
  private static readonly _BASEPRICE: number = 1;
  private static readonly _DAY: number = 2;
  private static readonly _DISCOUNT: number = 3;
  private static readonly _LATEFEE: number = 4;
  private static readonly _MINIMUMPRICE: number = 5;
  private static readonly _PRICEPERADVANCEDREQUEST: number = 6;
  private static readonly _TAXATION: number = 7;
  public id: number = 0;
  public otherMaster: DBObject;
  private _basePrice: number = 0.0;
  private _pricePerAdvancedRequest: number = 0.0;
  private _discount: number = 0.0;
  private _day: number = 1;
  private _minimumPrice: number = 0.0;
  private _taxation: number = 0.0;
  private _lateFee: number = 0.0;
  private _administrator: Administrator = null;
  public constructor(
    d3eParams?: Partial<{
      administrator: Administrator;
      basePrice: number;
      day: number;
      discount: number;
      lateFee: number;
      minimumPrice: number;
      pricePerAdvancedRequest: number;
      taxation: number;
    }>
  ) {
    super();

    this.setAdministrator(d3eParams?.administrator ?? null);

    this.setBasePrice(d3eParams?.basePrice ?? 0.0);

    this.setDay(d3eParams?.day ?? 1);

    this.setDiscount(d3eParams?.discount ?? 0.0);

    this.setLateFee(d3eParams?.lateFee ?? 0.0);

    this.setMinimumPrice(d3eParams?.minimumPrice ?? 0.0);

    this.setPricePerAdvancedRequest(d3eParams?.pricePerAdvancedRequest ?? 0.0);

    this.setTaxation(d3eParams?.taxation ?? 0.0);
  }
  public get d3eType(): string {
    return "InvoiceConfiguration";
  }
  public clear(): void {
    this.d3eChanges.clear();
  }
  public get basePrice(): number {
    return this._basePrice;
  }
  public setBasePrice(val: number): void {
    let isValChanged: boolean = this._basePrice !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(InvoiceConfiguration._BASEPRICE, this._basePrice);

    this._basePrice = val;

    this.fire("basePrice", this);
  }
  public get pricePerAdvancedRequest(): number {
    return this._pricePerAdvancedRequest;
  }
  public setPricePerAdvancedRequest(val: number): void {
    let isValChanged: boolean = this._pricePerAdvancedRequest !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(
      InvoiceConfiguration._PRICEPERADVANCEDREQUEST,
      this._pricePerAdvancedRequest
    );

    this._pricePerAdvancedRequest = val;

    this.fire("pricePerAdvancedRequest", this);
  }
  public get discount(): number {
    return this._discount;
  }
  public setDiscount(val: number): void {
    let isValChanged: boolean = this._discount !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(InvoiceConfiguration._DISCOUNT, this._discount);

    this._discount = val;

    this.fire("discount", this);
  }
  public get day(): number {
    return this._day;
  }
  public setDay(val: number): void {
    let isValChanged: boolean = this._day !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(InvoiceConfiguration._DAY, this._day);

    this._day = val;

    this.fire("day", this);
  }
  public get minimumPrice(): number {
    return this._minimumPrice;
  }
  public setMinimumPrice(val: number): void {
    let isValChanged: boolean = this._minimumPrice !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(
      InvoiceConfiguration._MINIMUMPRICE,
      this._minimumPrice
    );

    this._minimumPrice = val;

    this.fire("minimumPrice", this);
  }
  public get taxation(): number {
    return this._taxation;
  }
  public setTaxation(val: number): void {
    let isValChanged: boolean = this._taxation !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(InvoiceConfiguration._TAXATION, this._taxation);

    this._taxation = val;

    this.fire("taxation", this);
  }
  public get lateFee(): number {
    return this._lateFee;
  }
  public setLateFee(val: number): void {
    let isValChanged: boolean = this._lateFee !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(InvoiceConfiguration._LATEFEE, this._lateFee);

    this._lateFee = val;

    this.fire("lateFee", this);
  }
  public get administrator(): Administrator {
    return this._administrator;
  }
  public setAdministrator(val: Administrator): void {
    let isValChanged: boolean = this._administrator !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(
      InvoiceConfiguration._ADMINISTRATOR,
      this._administrator
    );

    this.updateObservable("administrator", this._administrator, val);

    this._administrator = val;

    this.fire("administrator", this);
  }
  public get(field: number): any {
    switch (field) {
      case InvoiceConfiguration._BASEPRICE: {
        return this._basePrice;
      }

      case InvoiceConfiguration._PRICEPERADVANCEDREQUEST: {
        return this._pricePerAdvancedRequest;
      }

      case InvoiceConfiguration._DISCOUNT: {
        return this._discount;
      }

      case InvoiceConfiguration._DAY: {
        return this._day;
      }

      case InvoiceConfiguration._MINIMUMPRICE: {
        return this._minimumPrice;
      }

      case InvoiceConfiguration._TAXATION: {
        return this._taxation;
      }

      case InvoiceConfiguration._LATEFEE: {
        return this._lateFee;
      }

      case InvoiceConfiguration._ADMINISTRATOR: {
        return this._administrator;
      }
      default: {
        return null;
      }
    }
  }
  public updateD3EChanges(index: number, value: any): void {
    if (this.lockedChanges()) {
      return;
    }

    super.updateD3EChanges(index, value);
  }
  public restore(): void {
    /*
TODO: Might be removed
*/

    this.d3eChanges.restore(this);
  }
  public deepClone(clearId = true): InvoiceConfiguration {
    let ctx: CloneContext = new CloneContext({ "clearId": clearId });

    return ctx.startClone(this);
  }
  public collectChildValues(ctx: CloneContext): void {}
  public deepCloneIntoObj(dbObj: DBObject, ctx: CloneContext): void {
    let obj: InvoiceConfiguration = dbObj as InvoiceConfiguration;

    obj.id = this.id;

    obj.setBasePrice(this._basePrice);

    obj.setPricePerAdvancedRequest(this._pricePerAdvancedRequest);

    obj.setDiscount(this._discount);

    obj.setDay(this._day);

    obj.setMinimumPrice(this._minimumPrice);

    obj.setTaxation(this._taxation);

    obj.setLateFee(this._lateFee);

    obj.setAdministrator(this._administrator);
  }
  public set(field: number, value: any): void {
    switch (field) {
      case InvoiceConfiguration._BASEPRICE: {
        this.setBasePrice(value as number);
        break;
      }

      case InvoiceConfiguration._PRICEPERADVANCEDREQUEST: {
        this.setPricePerAdvancedRequest(value as number);
        break;
      }

      case InvoiceConfiguration._DISCOUNT: {
        this.setDiscount(value as number);
        break;
      }

      case InvoiceConfiguration._DAY: {
        this.setDay(value as number);
        break;
      }

      case InvoiceConfiguration._MINIMUMPRICE: {
        this.setMinimumPrice(value as number);
        break;
      }

      case InvoiceConfiguration._TAXATION: {
        this.setTaxation(value as number);
        break;
      }

      case InvoiceConfiguration._LATEFEE: {
        this.setLateFee(value as number);
        break;
      }

      case InvoiceConfiguration._ADMINISTRATOR: {
        this.setAdministrator(value as Administrator);
        break;
      }
    }
  }
}
